import { apiGet, apiSend } from './api.js';
import type {
  FleetInstallation,
  InstallationSigningKey,
  BackupPosture,
  RegisterInstallationRequest,
  RotateKeyRequest,
  RevokeCompromisedKeyRequest,
} from '@hospasense/contracts';

export async function fetchFleetInstallations(params?: {
  organisationId?: string | undefined;
  ring?: string | undefined;
  state?: string | undefined;
  search?: string | undefined;
}): Promise<{ items: FleetInstallation[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.organisationId) query.set('organisationId', params.organisationId);
  if (params?.ring) query.set('ring', params.ring);
  if (params?.state) query.set('state', params.state);
  if (params?.search) query.set('search', params.search);
  const qStr = query.toString() ? `?${query.toString()}` : '';
  return apiGet<{ items: FleetInstallation[]; total: number }>(`/api/v1/fleet${qStr}`);
}

export async function fetchInstallation(identifier: string): Promise<FleetInstallation> {
  return apiGet<FleetInstallation>(`/api/v1/fleet/${identifier}`);
}

export async function registerInstallation(
  input: RegisterInstallationRequest,
): Promise<FleetInstallation> {
  return apiSend<FleetInstallation>('POST', '/api/v1/fleet/register', input);
}

export async function updateInstallation(
  identifier: string,
  input: {
    displayName?: string;
    rolloutRing?: 'internal' | 'pilot' | 'general';
    targetVersion?: string | null;
    integrationReadiness?: Record<string, boolean>;
    expectedVersion?: number;
  },
): Promise<FleetInstallation> {
  return apiSend<FleetInstallation>('PATCH', `/api/v1/fleet/${identifier}`, input);
}

export async function rotateSigningKey(
  identifier: string,
  input: RotateKeyRequest,
): Promise<InstallationSigningKey> {
  return apiSend<InstallationSigningKey>('POST', `/api/v1/fleet/${identifier}/rotate-key`, input);
}

export async function revokeSigningKey(
  identifier: string,
  input: RevokeCompromisedKeyRequest,
): Promise<{ success: boolean; message: string }> {
  return apiSend<{ success: boolean; message: string }>(
    'POST',
    `/api/v1/fleet/${identifier}/revoke-key`,
    input,
  );
}

export async function fetchSigningKeys(identifier: string): Promise<InstallationSigningKey[]> {
  const data = await apiGet<{ items: InstallationSigningKey[] }>(
    `/api/v1/fleet/${identifier}/keys`,
  );
  return data.items;
}

export async function fetchBackupPosture(identifier: string): Promise<BackupPosture> {
  return apiGet<BackupPosture>(`/api/v1/fleet/${identifier}/backup-posture`);
}

export async function fetchRecentHeartbeats(
  identifier: string,
): Promise<Array<{ id: string; timestamp: string; schemaVersion: string; payload: unknown }>> {
  const data = await apiGet<{
    items: Array<{ id: string; timestamp: string; schemaVersion: string; payload: unknown }>;
  }>(`/api/v1/telemetry/installations/${identifier}/heartbeats`);
  return data.items;
}
